// Wind Farm and Turbine Models
export interface WindFarm {
  id: string;
  name: string;
  location: { lat: number; lng: number };
  city: string;
  country: string;
  turbineCount: number;
  installedPower: number; // MW
  createdAt: string;
}

export interface Turbine {
  id: string;
  farmId: string;
  number: string;
  name: string;
  model: string;
  manufacturer: string;
  rotorDiameter: number;
  bladeModel: string;
  bladeLength: number;
  bladeDiameter: number;
  power: number;
  location: { lat: number; lng: number };
  turbineNumber: string;
  inspectionId: string | null;
  lastInspectionDate?: string;
  lastInspectionStatus?: 'completed' | 'in-progress' | 'pending';
  damageCount: number;
  createdAt?: string;
}

export interface Inspection {
  id: string;
  turbineId: string;
  date: string;
  status: 'completed' | 'in-progress' | 'pending';
  damageCount: number;
  projectId: string; // R2 project ID
  folderStructure?: FolderStructure;
}

// Image and Annotation Models
export interface ImageData {
  fileName: string;
  path: string;
  url: string;
  turbineName: string;
  bladeName: string;
  surfaceType: string;
  altitude?: number; // EXIF'ten gelen rakım bilgisi
  datetime?: string; // EXIF'ten gelen tarih bilgisi
  uploadDate?: Date;
}

export type AnnotationShape = 'rectangle' | 'circle' | 'polygon';

export interface Annotation {
  id: string;
  imageData: ImageData;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  comment: string;
  damageType?: DamageType;
  damageDescription?: string;
  description?: string;
  confidence?: number;
  severity?: number; // 1-5 arası hasar şiddeti
  suggestions?: string;
  aiAnalyzed?: boolean;
  shape?: AnnotationShape;
  points?: { x: number; y: number }[];
}

export interface FolderStructure {
  turbineName: string;
  blades: {
    [bladeName: string]: {
      surfaces: {
        [surfaceType: string]: string[];
      };
    };
  };
}

export enum DamageType {
  LEADING_EDGE_EROSION = "Leading Edge Erosion",
  LIGHTNING_DAMAGE = "Lightning Damage",
  SURFACE_CONTAMINATION = "Surface Contamination",
  STRUCTURAL_CRACK = "Structural Crack",
  COATING_DAMAGE = "Coating Damage"
}

export interface DamageDescription {
  description: string;
  severity: string;
  recommendations: string[];
}

export const damageDescriptions: Record<DamageType, DamageDescription> = {
  [DamageType.LEADING_EDGE_EROSION]: {
    description: "Kanadın ön kenarında malzeme kaybı ve pürüzlenme",
    severity: "Orta-Yüksek",
    recommendations: [
      "Koruyucu kaplama uygulanması",
      "Düzenli bakım ve kontrol",
      "Hasarlı bölgenin onarımı"
    ]
  },
  [DamageType.LIGHTNING_DAMAGE]: {
    description: "Yıldırım çarpması sonucu oluşan hasar, yanık izleri, delikler",
    severity: "Yüksek",
    recommendations: [
      "Yapısal bütünlük kontrolü",
      "Hasarlı bölgenin onarımı",
      "Yıldırım koruma sisteminin kontrolü"
    ]
  },
  [DamageType.SURFACE_CONTAMINATION]: {
    description: "Kir, tuz, biyolojik büyüme",
    severity: "Düşük-Orta",
    recommendations: [
      "Yüzey temizliği",
      "Koruyucu kaplama kontrolü",
      "Düzenli bakım programı"
    ]
  },
  [DamageType.STRUCTURAL_CRACK]: {
    description: "Kanadın yapısal bütünlüğünü etkileyen çatlaklar",
    severity: "Kritik",
    recommendations: [
      "Acil onarım",
      "Yapısal analiz",
      "Çatlak ilerlemesinin takibi"
    ]
  },
  [DamageType.COATING_DAMAGE]: {
    description: "Koruyucu kaplamanın soyulması, kabarması veya kaybı",
    severity: "Orta",
    recommendations: [
      "Kaplama yenileme",
      "Yüzey hazırlığı",
      "Nem ve korozyon kontrolü"
    ]
  }
};

// Inspection Database Record
export interface InspectionRecord {
  id: string;
  projectId: string; // Link to original project
  windFarmName: string;
  turbineName: string;
  bladeName: string;
  surfaceType: string;
  damageType: string;
  severity: number;
  description: string;
  imageUrl: string;
  thumbnailUrl?: string; // Optional: for faster loading in table
  comment?: string;
  status: 'detected' | 'verified' | 'repaired';
  createdAt: string;
  updatedAt: string;
}